import { FilterFnOption } from '@tanstack/react-table';
import { TJobFilters } from '../../organisms/JobFiltersModal/JobFiltersModal';
import { TEcowIsolation, TJob, TEmocEmoc, TEcowPermit } from '../../../lib/types';
import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_JOB_ASPECT_STATUSES, JOB_ASPECT_STATUS } from './Jobs.constants';
import {
  ECOW_ISOLATION_STATUS_MAPPING,
  ECOW_PERMIT_STATUS_MAPPING,
  GOC_STATUS_ENUM,
  INSULATION_STATUS_ENUM,
  JOB_FIELD_WALK_STATUS_ENUM,
  JOB_SCHEDULE_STATUS_ENUM,
  LEAK_TEST_PACK_STATUS_ENUM,
  LIFTPLAN_STATUS_ENUM,
  PROCEDURE_STATUS_ENUM,
  PURCHASE_ORDER_STATUS_ENUM,
  SCAFFOLD_STATUS_ENUM,
  WORKPACK_STATUS_ENUM,
} from '../../../lib/constants/dbConstants';
import { useSelectedAsset } from '../../../lib/util';
import { useLazyQuery, useQuery } from '@apollo/client';
import { GET_EMOC_BY_MOC_NUMBERS } from '../../../graph-queries/tar/emoc';
import { GET_ISOLATIONS_BY_ISOLATION_NUMBERS } from '../../../graph-queries/tar/isolation';
import { GET_PERMITS_BY_PERMIT_NUMBERS } from '../../../graph-queries/tar/permit';
import { PermitRequirementEnum } from '../../../graph-queries/__generated__/graphql';
import {
  getIsolationOptions,
  getLabelForIsolationStatus,
  getLabelForMocStatus,
  getLabelForPermitStatus,
  getMocOptions,
  getPermitOptions,
} from '../../organisms/JobFiltersModal/JobFiltersModal.util';

export const jobsGlobalFilterFn: FilterFnOption<TJob> = (
  row,
  _columnId,
  { filters, searchString }: { filters: TJobFilters; searchString: string }
) => {
  const { original } = row;

  if (
    // Search string doesn't match, skip the rows
    searchString &&
    !JSON.stringify(original).toLowerCase().includes(searchString.toLowerCase())
  ) {
    return false;
  }
  //filtering of status columns
  for (const statusType of [
    'workpackStatus',
    'scaffoldStatus',
    'insulationStatus',
    'leakTestPackStatus',
    'liftPlanStatus',
    'procedureStatus',
    'scheduleStatus',
    'gocStatus',
    'fieldWalkStatus',
    'purchaseOrderStatus',
  ] satisfies (keyof TJobFilters)[]) {
    const statusValue = original.aspectStatuses?.[statusType];
    if (
      // Aspect or schedule or goc status doesn't match, skip the rows
      filters[statusType].length > 0 &&
      !(statusValue && filters[statusType].includes(statusValue))
    )
      return false;
  }

  //filtering permitStatus
  if (filters['permitStatus']) {
    const permitStatusValue = original.aspectStatuses?.['permitStatus'] || '';
    const labeledPermitStatus = getLabelForPermitStatus(permitStatusValue);
    const statusValue = getPermitOptions().includes(labeledPermitStatus)
      ? labeledPermitStatus
      : 'Other';
    if (
      // Permit status doesn't match, skip the rows
      filters['permitStatus'].length > 0 &&
      !(statusValue && filters['permitStatus'].includes(statusValue))
    )
      return false;
  }

  //filtering isolationStatus
  if (filters['isolationStatus']) {
    const isolationStatusValue =
      original.aspectStatuses?.['isolationStatus'] === ''
        ? 'IsolationNumberRequired'
        : original.aspectStatuses?.['isolationStatus'] || '';
    const labeledIsolationStatus = getLabelForIsolationStatus(isolationStatusValue);
    const statusValue = getIsolationOptions().includes(labeledIsolationStatus)
      ? labeledIsolationStatus
      : Object.values(JOB_ASPECT_STATUS).includes(isolationStatusValue)
        ? isolationStatusValue
        : 'Other';
    if (
      // Isolation status doesn't match, skip the rows
      filters['isolationStatus'].length > 0 &&
      !(statusValue && filters['isolationStatus'].includes(statusValue))
    )
      return false;
  }

  //filtering mocStatus
  if (filters['mocStatus']) {
    const mocStatusValue = original.aspectStatuses?.['mocStatus'] || '';
    const labeledMocStatus = getLabelForMocStatus(mocStatusValue);
    const statusValue = getMocOptions().includes(labeledMocStatus) ? labeledMocStatus : 'Other';
    if (
      // MOC status doesn't match, skip the rows
      filters['mocStatus'].length > 0 &&
      !(statusValue && filters['mocStatus'].includes(statusValue))
    )
      return false;
  }

  //filtering jobId and assigned planner
  if (filters['jobId']) {
    const jobId = original?.jobId;
    const jobIdFilters = filters.jobId.map((str) => parseInt(str, 10));
    if (
      // Job Fields don't match, skip the rows
      filters['jobId'].length > 0 &&
      !(jobId && jobIdFilters.includes(jobId))
    )
      return false;
  }

  if (filters['assignedPlanner']) {
    const assignedPlanner = original?.assignedPlanner;
    if (
      // Job Fields don't match, skip the rows
      filters['assignedPlanner'].length > 0 &&
      !(
        assignedPlanner &&
        filters.assignedPlanner.some((planner) =>
          assignedPlanner.toLowerCase().includes(planner.toLowerCase())
        )
      )
    )
      return false;
  }
  if (filters['gocSh1']) {
    const gocSh1 = original?.tarGoc?.sh1;
    if (
      // Job Fields don't match, skip the rows
      filters['gocSh1'].length > 0 &&
      !(gocSh1 && filters.gocSh1.some((goc) => gocSh1.toLowerCase().includes(goc.toLowerCase())))
    )
      return false;
  }

  return true;
};

type StatusType = keyof Exclude<TJob['aspectStatuses'], undefined>;

const useEcowPermitsMap = (jobs: TJob[]) => {
  const [ecowPermitsMap, setEcowPermitsMap] = useState<Record<string, TEcowPermit>>({});
  const [getPermits] = useLazyQuery(GET_PERMITS_BY_PERMIT_NUMBERS);

  const permitNumbers = useMemo(() => {
    const permitNumbers = new Set<string>();
    jobs.forEach((job) =>
      job?.permits?.forEach((permit) => {
        if (permit.permitRequirement === PermitRequirementEnum.Required) {
          permitNumbers.add(permit.permitNumber);
        }
      })
    );
    return [...permitNumbers];
  }, [jobs]);

  useEffect(() => {
    if (permitNumbers.length > 0) {
      getPermits({ variables: { permitNumbers } }).then((res) => {
        if (res.data?.permitsByPermitNumbers) {
          const permitsMap = res.data.permitsByPermitNumbers.reduce(
            (map: Record<string, TEcowPermit>, permit: TEcowPermit) => {
              map[permit.permitNumber] = permit;
              return map;
            },
            {}
          );
          setEcowPermitsMap(permitsMap);
        }
      });
    }
  }, [permitNumbers]);

  return ecowPermitsMap;
};

export const useJobsWithTrackerStatuses = (
  jobs: TJob[],
  ecowIsolations: TEcowIsolation[],
  eMoc: TEmocEmoc[]
) => {
  const ecowPermitsMap = useEcowPermitsMap(jobs);
  return useMemo(() => {
    const isolationsMap = new Map(
      (ecowIsolations || []).map((isolation) => [
        isolation.isolationNumber,
        isolation.statusComment,
      ])
    );
    const mocMap = new Map((eMoc || []).map((moc) => [moc.mocNumber, moc.mocStatus]));

    return jobs.map((job) => ({
      ...job,
      aspectStatuses: {
        ...(job.aspectStatuses || DEFAULT_JOB_ASPECT_STATUSES),
        isolationStatus: calculateIsolationStatus(job, isolationsMap),
        mocStatus: calculateMocStatus(job, mocMap),
        permitStatus: calculatePermitStatus(job, ecowPermitsMap),
        ...{
          ...Object.fromEntries(
            (
              [
                'workpackStatus',
                'scaffoldStatus',
                'gocStatus',
                'scheduleStatus',
                'insulationStatus',
                'procedureStatus',
                'leakTestPackStatus',
                'liftPlanStatus',
                'fieldWalkStatus',
                'purchaseOrderStatus',
              ] as StatusType[]
            ).map((item) => [item, calculateStatus(job, item)])
          ),
        },
      } as TJob['aspectStatuses'],
    }));
  }, [jobs, ecowIsolations, eMoc, ecowPermitsMap]);
};

export const useJobEmocs = (jobs: TJob[]) => {
  const selectedAsset = useSelectedAsset();
  const uniqueMOCNumbers = useMemo(
    () => [
      ...new Set(
        jobs
          .filter((job) => job.mocRequired)
          .flatMap((job) => job.tarMoc || [])
          .map((moc) => moc?.mocNumber)
          .filter((mocNumber) => mocNumber !== undefined) as unknown as string[]
      ),
    ],
    [jobs]
  );

  const { data: emocData } = useQuery(GET_EMOC_BY_MOC_NUMBERS, {
    variables: {
      mocNumbers: uniqueMOCNumbers.join(',') as unknown as string,
      assetId: selectedAsset?.assetId || -1,
    },
    skip: !(uniqueMOCNumbers && uniqueMOCNumbers.length > 0 && selectedAsset?.assetId),
  });
  const eMocs = (emocData?.emocsByEmocNumbers as unknown as TEmocEmoc[]) || [];
  return eMocs;
};

export const useJobIsolations = (jobs: TJob[]) => {
  const uniqueIsolationNumbers = useMemo(
    () => [
      ...new Set(
        jobs
          .filter((job) => job.isolationRequired)
          .flatMap((job) => job.tarIsolations || [])
          .map((isolation) => isolation?.isolationNumber)
          .filter((isolationNumber) => isolationNumber !== undefined)
      ),
    ],
    [jobs]
  );
  const { data: isolationData } = useQuery(GET_ISOLATIONS_BY_ISOLATION_NUMBERS, {
    variables: { isolationNumbers: uniqueIsolationNumbers as unknown as string[] },
    skip: !uniqueIsolationNumbers,
  });
  const ecowIsolations =
    (isolationData?.isolationsByIsolationNumbers as unknown as TEcowIsolation[]) || [];
  return ecowIsolations;
};
const calculateMocStatus = (job: TJob, mocMap: Map<string, string>): string => {
  if (job.mocRequired === false) {
    return JOB_ASPECT_STATUS.NOT_REQUIRED;
  }
  if (!job.mocRequired) {
    return JOB_ASPECT_STATUS.SETUP_REQUIRED;
  }

  if (!job.tarMoc) {
    //MOC -> Job is 1:M
    return JOB_ASPECT_STATUS.LINK_REQUIRED;
  }
  const mocNumber = job?.tarMoc?.mocNumber || '';
  const currentStatus = mocMap.get(mocNumber) || '';
  return currentStatus;
};

const calculateIsolationStatus = (job: TJob, isolationsMap: Map<string, string>): string => {
  if (job.isolationRequired === false) {
    return JOB_ASPECT_STATUS.NOT_REQUIRED;
  }
  if (!job.isolationRequired) {
    return JOB_ASPECT_STATUS.SETUP_REQUIRED;
  }

  if (!job.tarIsolations || job.tarIsolations.length === 0) {
    return JOB_ASPECT_STATUS.LINK_REQUIRED;
  }

  const isolationStatusPriorityMap: Record<string, number> = {
    ...Object.fromEntries(
      Object.entries(ECOW_ISOLATION_STATUS_MAPPING).map(([status, details]) => {
        return [status.toLowerCase(), details.priority];
      })
    ),
    '': 100,
  };

  let leastProgressedStatus = '';

  job?.tarIsolations.forEach((isolation) => {
    const currentStatus = isolationsMap.get(isolation.isolationNumber) || '';
    if (currentStatus.toLowerCase() in isolationStatusPriorityMap) {
      if (
        isolationStatusPriorityMap[currentStatus.toLowerCase()] <
        isolationStatusPriorityMap[leastProgressedStatus]
      ) {
        leastProgressedStatus = currentStatus;
      }
    }
  });

  return leastProgressedStatus;
};

const calculatePermitStatus = (job: TJob, ecowPermitsMap: Record<string, TEcowPermit>) => {
  // if some of the permits are not setup
  if (
    job.permits?.some(
      (permit) =>
        permit.permitRequirement !== PermitRequirementEnum.Required &&
        permit.permitRequirement !== PermitRequirementEnum.NotRequired
    )
  )
    return JOB_ASPECT_STATUS.SETUP_REQUIRED;

  if (
    job.permits?.every((permit) => permit.permitRequirement === PermitRequirementEnum.NotRequired)
  )
    return JOB_ASPECT_STATUS.NOT_REQUIRED;

  if (
    job.permits?.some(
      (permit) =>
        permit.permitRequirement === PermitRequirementEnum.Required && !permit.permitNumber
    )
  )
    return 'Required';

  const requiredPermits = (job.permits || []).filter(
    (permit) => permit.permitRequirement === PermitRequirementEnum.Required
  );

  const permitStatusPriorityMap: Record<string, number> = {
    ...Object.fromEntries(
      Object.entries(ECOW_PERMIT_STATUS_MAPPING).map(([status, details]) => {
        return [status.toLowerCase(), details.priority];
      })
    ),
    '': 100,
  };

  let leastProgressedStatus = '';

  requiredPermits?.forEach((permit) => {
    const currentStatus = ecowPermitsMap?.[permit.permitNumber]?.statusComment || '';
    if (currentStatus.toLowerCase() in permitStatusPriorityMap) {
      if (
        permitStatusPriorityMap[currentStatus.toLowerCase()] <
        permitStatusPriorityMap[leastProgressedStatus]
      ) {
        leastProgressedStatus = currentStatus;
      }
    }
  });

  return leastProgressedStatus;
};

const calculateStatus = (row: TJob, statusType: StatusType): string => {
  switch (statusType) {
    case 'gocStatus':
      return (row.gocStatus && GOC_STATUS_ENUM[row.gocStatus]) || row.gocStatus || '';
    case 'fieldWalkStatus':
      return (row.fieldWalk && JOB_FIELD_WALK_STATUS_ENUM[row.fieldWalk]) || row.fieldWalk;
    case 'scheduleStatus':
      return (
        (row.scheduleStatus && JOB_SCHEDULE_STATUS_ENUM[row.scheduleStatus]) || row.scheduleStatus
      );
    case 'workpackStatus':
      if (row.workpackRequired) {
        return row.leastProgressedWorkpack
          ? WORKPACK_STATUS_ENUM[row.leastProgressedWorkpack.status]
          : JOB_ASPECT_STATUS.LINK_REQUIRED;
      } else if (row.workpackRequired === false) {
        return JOB_ASPECT_STATUS.NOT_REQUIRED;
      } else {
        return JOB_ASPECT_STATUS.SETUP_REQUIRED;
      }
    case 'scaffoldStatus':
      if (row.scaffoldRequired) {
        return row.leastProgressedScaffold
          ? SCAFFOLD_STATUS_ENUM[row.leastProgressedScaffold.status]
          : JOB_ASPECT_STATUS.LINK_REQUIRED;
      } else if (row.scaffoldRequired === false) {
        return JOB_ASPECT_STATUS.NOT_REQUIRED;
      } else {
        return JOB_ASPECT_STATUS.SETUP_REQUIRED;
      }
    case 'insulationStatus':
      if (row.insulationRequired) {
        return row.leastProgressedInsulation
          ? INSULATION_STATUS_ENUM[row.leastProgressedInsulation.status]
          : JOB_ASPECT_STATUS.LINK_REQUIRED;
      } else if (row.insulationRequired === false) {
        return JOB_ASPECT_STATUS.NOT_REQUIRED;
      } else {
        return JOB_ASPECT_STATUS.SETUP_REQUIRED;
      }
    case 'leakTestPackStatus':
      return row.leastProgressedLeakTestPack
        ? LEAK_TEST_PACK_STATUS_ENUM[row.leastProgressedLeakTestPack.status]
        : JOB_ASPECT_STATUS.LINK_REQUIRED;

    case 'liftPlanStatus':
      if (row.liftPlanRequired) {
        return row.leastProgressedLiftPlan
          ? LIFTPLAN_STATUS_ENUM[row.leastProgressedLiftPlan.status]
          : JOB_ASPECT_STATUS.LINK_REQUIRED;
      } else if (row.liftPlanRequired === false) {
        return JOB_ASPECT_STATUS.NOT_REQUIRED;
      } else {
        return JOB_ASPECT_STATUS.SETUP_REQUIRED;
      }
    case 'procedureStatus':
      return row.leastProgressedProcedure
        ? PROCEDURE_STATUS_ENUM[row.leastProgressedProcedure.status]
        : JOB_ASPECT_STATUS.LINK_REQUIRED;
    case 'purchaseOrderStatus':
      if (
        row.specialistToolingRequired === false &&
        row.specialistVendorsRequired === false &&
        row.materialsRequired === false
      ) {
        return JOB_ASPECT_STATUS.NOT_REQUIRED;
      } else if (
        row.specialistToolingRequired ||
        row.specialistVendorsRequired ||
        row.materialsRequired
      ) {
        return row.leastProgressedPurchaseOrder
          ? PURCHASE_ORDER_STATUS_ENUM[row.leastProgressedPurchaseOrder.status]
          : JOB_ASPECT_STATUS.LINK_REQUIRED;
      } else {
        return JOB_ASPECT_STATUS.SETUP_REQUIRED;
      }
  }
  return JOB_ASPECT_STATUS.SETUP_REQUIRED;
};
